import { Directive, ElementRef, HostListener, Renderer2 } from '@angular/core';
import { country } from '../enums/language';
import { LanguageService } from '../services/language.service';
import { NgControl } from '@angular/forms';

interface NumberFormatSettings {
  decimalSeparator: string;
  thousandSeparator: string;
  countryDomain: string;
  isCountryIndo: boolean;
  maxDecimal: number;
  acceptLanguage: string;
}

@Directive({
  selector: '[formatInputNumber]',
})
export class NumberFormatDirective {
  private settings: NumberFormatSettings;

  constructor(
    private _elementRef: ElementRef,
    private _renderer: Renderer2,
    private _languageService: LanguageService,
    private _control: NgControl
  ) {
    this.settings = this.loadSettings();
  }

  private loadSettings(): NumberFormatSettings {
    const countryDomain = this._languageService.getDomain();
    return {
      decimalSeparator: country[countryDomain].decimalSeparator,
      thousandSeparator: country[countryDomain].thousandSeparator,
      countryDomain,
      isCountryIndo: this._languageService.getCountry() === country.id.country,
      maxDecimal: country[countryDomain].maxDecimal,
      acceptLanguage: country[countryDomain].acceptLanguage,
    };
  }

  @HostListener('input', ['$event'])
  onInput(event,inputType?:string) {
    const inputElement = event.target as HTMLInputElement;
    let inputValue = inputElement?.value.trim();

    if(inputType==='deleteContentBackward'){
      event.inputType = inputType;
    }
    const inputTypeArr = ['insertFromPaste','deleteContentBackward'];
    if(this._control?.control && inputTypeArr.includes(event?.inputType)){
      inputValue = inputValue.replace(/^[^\d]+/, '');
      const formattedInputvalue = this.formatInputNumber(inputValue);
      if(formattedInputvalue && formattedInputvalue!== 'NaN'){
        inputValue = formattedInputvalue;
      }
      inputElement.value = inputValue;
      const currentErrors = this._control?.control?.errors;
      if (!this.isValidInput(inputValue)) {
        this._control.control.setErrors({ ...currentErrors, invalidNumber: true });
        if(!inputValue){
          const {invalidNumber, ...remainingErrors} = currentErrors;
          this._control.control.setErrors(remainingErrors);
        }
      }
    }

    if (!inputValue) return;

    // if user has input any invalid charactar, then don't format number and remove invalid char
    if (!this.isValidInput(inputValue)) {
      return this.preventInvalidInput(event);
    }

    const inputParts = inputValue.split(this.settings.decimalSeparator);
    const decimalPart = inputParts[1];

    // Check if the input contains more than one decimal separator or if the decimal part exceeds the maximum allowed
    const isInvalid =
      inputParts.length > 2 ||
      (decimalPart && decimalPart.length > this.settings.maxDecimal);

    if (isInvalid) {
      // Prevent invalid input and further processing
      return this.preventInvalidInput(event);
    }

    // Allow user to enter decimal or one zero or two zero after decimal
    if (['', '0', '00'].includes(decimalPart)) return;

    // Format the input based on country
    const formattedNumber = this.formatInputNumber(inputValue);

    // Update the input with formatted value
    this.updateInputValue(formattedNumber);
  }

  // Check if the input contains valid characters (0-9, '.', ',')
  private isValidInput(inputValue: string): boolean {
    // Check if the input value is a number separator ('.' for Indonesia, ',' for other countries)
    const isInvalidInput =
      (this.settings.isCountryIndo && inputValue === '.') ||
      (!this.settings.isCountryIndo && inputValue === ',');

    // If it's an invalid input, return false to prevent it
    if (isInvalidInput) return false;

    if(inputValue === '.'){
      const currentErrors = this._control?.control?.errors;
      this._control.control.setErrors({...currentErrors, invalidNumber:true});
    }

    // Use a regular expression to validate input for numbers and valid separators
    const validCharRegex = /^[0-9,.\b]+$/;
    const isValidChar =  validCharRegex.test(inputValue);
    if(!isValidChar){
      const currentErrors = this._control?.control?.errors;
      this._control.control.setErrors({...currentErrors, invalidNumber:true});
    }
    return isValidChar;
  }

  // Prevent invalid input characters and remove the last character entered
  private preventInvalidInput(event): boolean {
    const inputElement = event.target as HTMLInputElement;
    const inputTypeArr = ['insertFromPaste','deleteContentBackward'];
    if(!(inputTypeArr.includes(event?.inputType))){
      inputElement.value = inputElement.value.slice(0, -1);
    }
    event.preventDefault(); // Prevent default behavior
    event.stopPropagation(); // Stop event propagation
    return false;
  }

  // Format the input number based on country-specific formatting
  private formatInputNumber(inputValue: string): string {
    const formattedValue = this.parseInputValue(inputValue);
    const formatOptions = {
      maximumFractionDigits: this.settings.maxDecimal,
      minimumFractionDigits: 0,
    };
    return new Intl.NumberFormat(
      this.settings.acceptLanguage,
      formatOptions
    ).format(formattedValue);
  }

  // Parse the input value considering separators based on country
  private parseInputValue(inputValue: string): number {
    let formattedValue: number;

    if (this.settings.isCountryIndo) {
      // Replace thousand separators and convert the decimal separator to a dot ('.') for parsing
      formattedValue = parseFloat(
        inputValue
          .replace(new RegExp('\\' + this.settings.thousandSeparator, 'g'), '')
          .replace(new RegExp('\\' + this.settings.decimalSeparator), '.')
      );
    } else {
      // remove number separator 'commas' and parse the value
      formattedValue = parseFloat(inputValue.replace(/,/g, ''));
    }
    return formattedValue;
  }

  // Update the input field with the formatted value and trigger 'input' event
  private updateInputValue(newValue: string): void {
    if (this._elementRef.nativeElement.value !== newValue) {
      this._renderer.setProperty(
        this._elementRef.nativeElement,
        'value',
        newValue
      );
      this._elementRef.nativeElement.dispatchEvent(new Event('input'));
    }
  }
}
